import React, { useState, useEffect } from 'react';
import './StackViewer.css';
import axios from 'axios';
import { OrganSegAPIEndpoint } from '../../helper/APIFunctions';

function StackViewer({ organ, sliceInfo, isDemo = false }) {
  const [currentSlice, setCurrentSlice] = useState(0);
  const [sliceImage, setSliceImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [axis, setAxis] = useState('axial');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [showControls, setShowControls] = useState(false);

  // Note: Demo mode is not supported for StackViewer (mesh only)
  // StackViewer is hidden when isDemo=true in the parent component

  useEffect(() => {
    if (sliceInfo && sliceInfo.num_slices) {
      setCurrentSlice(Math.floor(sliceInfo.num_slices / 2));
    }
  }, [sliceInfo]);

  useEffect(() => {
    if (sliceInfo) {
      loadSlice(currentSlice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlice, axis, organ]);

  const loadSlice = async (sliceIdx) => {
    setLoading(true);
    try {
      // Load from EC2 backend
      const response = await axios.get(`${OrganSegAPIEndpoint}/api/slice/${organ}/${sliceIdx}`, {
        params: { axis }
      });
      setSliceImage(response.data.image);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSliceChange = (e) => {
    setCurrentSlice(parseInt(e.target.value));
  };

  const handleAxisChange = (newAxis) => {
    setAxis(newAxis);
    if (sliceInfo) {
      const axisMap = { sagittal: 0, coronal: 1, axial: 2 };
      const axisIdx = axisMap[newAxis];
      setCurrentSlice(Math.floor(sliceInfo.shape[axisIdx] / 2));
    }
  };

  if (!sliceInfo) {
    return <div className="stack-viewer-empty">Loading slice data...</div>;
  }

  const numSlices = sliceInfo.num_slices;

  return (
    <div className="stack-viewer">
      <div className="viewer-controls">
        <div className="axis-selector">
          <button
            className={`axis-button ${axis === 'axial' ? 'active' : ''}`}
            onClick={() => handleAxisChange('axial')}
          >
            Axial
          </button>
          <button
            className={`axis-button ${axis === 'coronal' ? 'active' : ''}`}
            onClick={() => handleAxisChange('coronal')}
          >
            Coronal
          </button>
          <button
            className={`axis-button ${axis === 'sagittal' ? 'active' : ''}`}
            onClick={() => handleAxisChange('sagittal')}
          >
            Sagittal
          </button>
        </div>

        <button
          className="contrast-toggle-btn"
          onClick={() => setShowControls(!showControls)}
          title="Image Contrast Controls"
        >
          Contrast Settings
        </button>
      </div>

      {showControls && (
        <div className="contrast-controls">
          <div className="control-group">
            <label htmlFor="brightness-slider">
              <span className="control-icon">☀️</span>
              Brightness: <strong>{brightness}%</strong>
            </label>
            <input
              id="brightness-slider"
              type="range"
              min="0"
              max="200"
              value={brightness}
              onChange={(e) => setBrightness(parseInt(e.target.value))}
              className="image-control-slider"
            />
            <button
              className="reset-btn"
              onClick={() => setBrightness(100)}
              title="Reset brightness"
            >
              ↺
            </button>
          </div>

          <div className="control-group">
            <label htmlFor="contrast-slider">
              <span className="control-icon">◐</span>
              Contrast: <strong>{contrast}%</strong>
            </label>
            <input
              id="contrast-slider"
              type="range"
              min="0"
              max="200"
              value={contrast}
              onChange={(e) => setContrast(parseInt(e.target.value))}
              className="image-control-slider"
            />
            <button
              className="reset-btn"
              onClick={() => setContrast(100)}
              title="Reset contrast"
            >
              ↺
            </button>
          </div>

          <button
            className="reset-all-btn"
            onClick={() => {
              setBrightness(100);
              setContrast(100);
            }}
          >
            Reset All
          </button>
        </div>
      )}

      <div className="image-container">
        {loading && <div className="loading-overlay">Loading...</div>}
        {sliceImage && (
          <img
            src={sliceImage}
            alt={`Slice ${currentSlice}`}
            className="slice-image"
            style={{
              filter: `brightness(${brightness}%) contrast(${contrast}%)`,
              transition: 'filter 0.1s ease'
            }}
          />
        )}
        {!sliceImage && !loading && (
          <div className="no-image">No image loaded</div>
        )}
      </div>

      <div className="slice-controls">
        <div className="slice-info">
          <span className="slice-label">Slice:</span>
          <span className="slice-number">{currentSlice + 1} / {numSlices}</span>
        </div>
        <div className="slider-container">
          <button
            className="nav-button"
            onClick={() => setCurrentSlice(Math.max(0, currentSlice - 1))}
            disabled={currentSlice === 0}
          >
            ‹
          </button>
          <input
            type="range"
            min="0"
            max={numSlices - 1}
            value={currentSlice}
            onChange={handleSliceChange}
            className="slice-slider"
          />
          <button
            className="nav-button"
            onClick={() => setCurrentSlice(Math.min(numSlices - 1, currentSlice + 1))}
            disabled={currentSlice === numSlices - 1}
          >
            ›
          </button>
        </div>
        <div className="metadata">
          <div className="meta-item">
            <span className="meta-label">Dimensions:</span>
            <span className="meta-value">
              {sliceInfo.shape[0]} × {sliceInfo.shape[1]} × {sliceInfo.shape[2]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StackViewer;

